/**
 * Two-step capability → adapter binding picker modal (P4.T4).
 *
 * Step 1: pick a capability (filtered to non-bound — already-bound
 * capabilities are managed inline on the ToolsCard rows via the rebind
 * Select + remove button).
 * Step 2: pick an adapter (filtered to those that implement the chosen
 * capability via `adapter.implements.includes(capability_id)`).
 *
 * Click an adapter → `composerStore.bindTool(capability_id, adapter_id)`
 * → close modal. Internal state resets on close so a re-open starts at
 * step 1.
 *
 * Reuses `useCapabilities()` (P2.T2) + `useAdapters()` (P2.T4) verbatim;
 * mirrors the two-step flow from the admin surface's
 * `PersonaToolBindings.AddBindingPicker` (P2.T9) inside the Composer's
 * modal shell.
 *
 * P4.T9 ("Explain why" affordances) will layer a satisfaction indicator
 * onto step-1's per-capability rows reading the same data this modal
 * already pulls — additive DOM, no rework.
 */
import { useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
} from '@/components/ui/Modal'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Skeleton } from '@/components/ui'
import { useCapabilities } from '@/hooks/useCapabilities'
import { useAdapters } from '@/hooks/useAdapters'
import { useComposerStore } from '@/stores/composerStore'
import type { Capability, Adapter } from '@/types/toolFramework'

export interface ToolBindingPickerModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ToolBindingPickerModal({ open, onOpenChange }: ToolBindingPickerModalProps) {
  const [selectedCapabilityId, setSelectedCapabilityId] = useState<string>('')
  const draft = useComposerStore((s) => s.draftBundle)
  const server = useComposerStore((s) => s.serverBundle)
  const bindTool = useComposerStore((s) => s.bindTool)
  const caps = useCapabilities()
  const adapters = useAdapters()

  const base = draft ?? server
  const boundIds = new Set<string>(
    base ? Object.keys(base.tool_bindings) : [],
  )

  const isLoading = caps.isLoading || adapters.isLoading
  const hasError = caps.isError || adapters.isError

  function resetAndClose() {
    setSelectedCapabilityId('')
    onOpenChange(false)
  }

  function handleOpenChange(next: boolean) {
    if (next) onOpenChange(true)
    else resetAndClose()
  }

  function handlePickAdapter(adapter_id: string) {
    if (!selectedCapabilityId) return
    bindTool(selectedCapabilityId, adapter_id)
    resetAndClose()
  }

  function handleRetry() {
    caps.refetch()
    adapters.refetch()
  }

  const selectedCapability =
    selectedCapabilityId && caps.data
      ? caps.data.find((c) => c.id === selectedCapabilityId)
      : undefined

  return (
    <Modal open={open} onOpenChange={handleOpenChange}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            {selectedCapabilityId === '' ? 'Add a tool — choose a capability' : 'Choose an adapter'}
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          {hasError && (
            <Alert variant="destructive" className="text-sm mb-3">
              <div className="flex items-center justify-between gap-3">
                <span>Unable to load the tool catalog.</span>
                <Button variant="ghost" size="sm" onClick={handleRetry}>
                  Retry
                </Button>
              </div>
            </Alert>
          )}

          {isLoading && (
            <div className="space-y-3" data-testid="catalog-loading">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 rounded-sm" />
              ))}
            </div>
          )}

          {!isLoading && !hasError && selectedCapabilityId === '' && (
            <Step1
              capabilities={caps.data ?? []}
              boundIds={boundIds}
              onPick={setSelectedCapabilityId}
            />
          )}

          {!isLoading && !hasError && selectedCapabilityId !== '' && selectedCapability && (
            <Step2
              capability={selectedCapability}
              adapters={(adapters.data ?? []).filter((a) =>
                a.implements.includes(selectedCapabilityId),
              )}
              onBack={() => setSelectedCapabilityId('')}
              onPick={handlePickAdapter}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

function Step1({
  capabilities,
  boundIds,
  onPick,
}: {
  capabilities: Capability[]
  boundIds: Set<string>
  onPick: (id: string) => void
}) {
  const unbound = capabilities.filter((c) => !boundIds.has(c.id))
  if (unbound.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-6 text-center">
        All available capabilities are already bound. Remove one from the card to add a different binding.
      </p>
    )
  }
  return (
    <ul className="space-y-2">
      {unbound.map((cap) => (
        <li key={cap.id}>
          <button
            type="button"
            onClick={() => onPick(cap.id)}
            className="w-full text-left p-3 rounded-sm border border-border hover:bg-muted transition"
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-mono text-sm font-medium text-foreground">
                {cap.id}
              </span>
              <Badge variant="secondary" className="shrink-0">
                {cap.category}
              </Badge>
            </div>
            {cap.description && (
              <p className="text-xs text-muted-foreground mt-1">
                {cap.description}
              </p>
            )}
          </button>
        </li>
      ))}
    </ul>
  )
}

function Step2({
  capability,
  adapters,
  onBack,
  onPick,
}: {
  capability: Capability
  adapters: Adapter[]
  onBack: () => void
  onPick: (adapter_id: string) => void
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="text-xs text-primary hover:text-primary/90 inline-flex items-center gap-1 mb-3"
      >
        <ArrowLeft className="w-3 h-3" /> Back to capabilities
      </button>
      <div className="rounded-md bg-muted p-3 mb-3">
        <span className="font-mono text-sm font-medium text-foreground">
          {capability.id}
        </span>
        {capability.description && (
          <p className="text-xs text-muted-foreground mt-1">
            {capability.description}
          </p>
        )}
      </div>
      {adapters.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">
          No adapters implement this capability. Install one via /admin/tools.
        </p>
      ) : (
        <ul className="space-y-2">
          {adapters.map((adapter) => (
            <li key={adapter.id}>
              <button
                type="button"
                onClick={() => onPick(adapter.id)}
                className="w-full text-left p-3 rounded-sm border border-border hover:bg-muted transition"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">
                    {adapter.display_name}
                  </span>
                  <Badge variant="outline" className="text-xs">
                    {adapter.kind}
                  </Badge>
                </div>
                <span className="font-mono text-xs text-muted-foreground">
                  {adapter.id}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
